import { useCallback, useEffect, useState } from "react";
import { Box, Stack, Text, Heading, Grid } from "@chakra-ui/react";
import * as Joi from "joi";

import useForm, { OnFormSubmitType } from "hooks/useForm";
import useGlobalState from "hooks/useGlobalState";
import useApiEndpoint from "hooks/useApiEndpoint";
import useRouter from "hooks/useRouter";
import GetContactInfo, {
  IExternalUrl,
} from "utils/messages/api/GetContactInfo";
import StartConversationApiEndpoint from "utils/messages/api/StartConversation";
import { IContactInfo } from "utils/offer";
import Container from "components/Chakra/Container";
import CustomLink from "components/Chakra/CustomLink";
import Button from "components/Chakra/Button";
import ContactAvatar from "components/ContactAvatar";
import { useTracking } from "contexts/Tracking";
import { ESessionItem } from "helpers/sessions";
import { getUniqVisitorId } from "helpers/cookies";
import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import FormIN from "scenes/OfferDetail/components/INContainer/FormIN";
import { useCreateModal, InitialUseCreateModalType } from "../ModalsProvider";
import { useLoginModal } from "../useLoginModal";
import { useMessageSentModal } from "../useMessageSentModal";
import CustomSpinner from "components/CustomSpinner";
import { SuspiciousOffer } from "components/SuspiciousOffer/SuspiciousOffer";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Textarea from "components/Chakra/Textarea";
import IconCrown from "components/Chakra/Icons/Crown";
import IconLink from "components/Chakra/Icons/Link";
import styles from "./index.module.css";
import { usePostUserEmailExistsMutation } from "@ud/api/phpApi/__generated__/authApi.generated";
import { getPageType, sendMessageConversion } from "helpers/conversions";
import { EventPayload } from "src/types/types";
import NewDatePicker from "components/Chakra/NewDatePicker";
import Checkbox from "components/Chakra/Checkbox";
import ChangeRenterCardApiEndpoint from "utils/user/api/ChangeRenterCard";
import useGlobalActions from "hooks/useGlobalActions";
import { useFlagsmith } from "contexts/Flagsmith";

type ContactModalProps = EventPayload & {
  offerId: number;
  eLab?: string;
  isLocked?: boolean;
  setOffersContacted?: (newValue: number) => void;
  isWatchdog?: boolean;
  isRelated?: boolean;
  isPin?: boolean;
  isSuspicious?: boolean;
};
interface IFormValues {
  email: string | undefined;
  message: string;
  includePersonalProfile: boolean;
  phone: { prefix: string; number: string };
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  availableFrom: string;
  isUpdateProfile: boolean;
}

const createTimeString = (seconds: number): string =>
  Math.floor(seconds / 3600) +
  ":" +
  ("0" + (Math.floor(seconds / 60) % 60)).slice(-2) +
  ":" +
  ("0" + (seconds % 60)).slice(-2);

const FormSchema = Joi.object({
  email: Joi.string().email({ tlds: false }).required(),
  message: Joi.string().min(10).required(),
  includePersonalProfile: Joi.boolean(),
  phone: JoiPhoneValidator(true),
  isUpdateProfile: Joi.boolean(),
});

const currentYear = new Date().getFullYear() - 15;

const FormSchemaBVariant = Joi.object({
  email: Joi.string().email({ tlds: false }).required(),
  message: Joi.string().min(10).required(),
  includePersonalProfile: Joi.boolean(),
  phone: JoiPhoneValidator(true),
  lastName: Joi.string().min(2).max(30).required(),
  firstName: Joi.string().min(2).max(30).required(),
  dateOfBirth: Joi.number().min(1920).max(currentYear).required(),
  availableFrom: Joi.string().isoDate().required(),
  isUpdateProfile: Joi.boolean(),
});

const ContactModal: React.FC<ContactModalProps> = ({
  offerId,
  isLocked,
  setOffersContacted,
  isWatchdog,
  isRelated,
  loginEvent,
  isPin,
  isSuspicious,
}) => {
  const { user } = useGlobalState();
  const { refreshUser } = useGlobalActions();
  const { navigateTo, pathname, asPath } = useRouter();
  const { trackEvent } = useTracking();
  const [url, setUrl] = useState<IExternalUrl | null>(null);
  const [info, setInfo] = useState<IContactInfo | null>(null);
  const [isEmail, setIsEmail] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState<number | undefined>(undefined);

  const { callEndpoint: getContactInfoApi } = useApiEndpoint(GetContactInfo);
  const [isUserExistsApi] = usePostUserEmailExistsMutation();
  const { callEndpoint: startConversationApi } = useApiEndpoint(
    StartConversationApiEndpoint,
  );
  const { callEndpoint: changeRenterCardApi } = useApiEndpoint(
    ChangeRenterCardApiEndpoint,
  );

  const { openModal: openLoginModal, closeAllModals } = useLoginModal();
  const { openModal: openMessageSentModal } = useMessageSentModal();
  const { getFlagByName } = useFlagsmith();

  const flag = getFlagByName("popup_contact_extended");

  const page = pathname.includes("inzerat") ? "offer_detail" : "search";
  const isFlagAlternative =
    flag && flag.enabled && flag.value === "popup_contact_extended";

  useEffect(() => {
    let eCat = "dialog.shown";
    if (isPin) {
      eCat = "search";
    }
    if (isWatchdog) {
      eCat = "profile";
    }
    trackEvent({
      "dl.eCat": eCat,
      "dl.eAct": isWatchdog || isPin ? "dialog.shown" : "offer_detail",
      "dl.eLab": isPin ? "Kontaktovat z pinu" : "Kontaktovat",
    });
  }, [trackEvent, isPin, isWatchdog]);

  const initialValues: IFormValues = {
    email: user?.email ?? "",
    message: user?.renter?.informations ?? "",
    includePersonalProfile: true,
    phone: user?.renter?.phone ?? { number: "", prefix: "+420" },
    firstName: user?.renter?.firstName ?? "",
    dateOfBirth: user?.renter?.birth ?? "",
    availableFrom: user?.renter?.rentStartAt ?? "",
    lastName: user?.renter?.lastName ?? "",
    isUpdateProfile: false,
  };

  useEffect(() => {
    if (flag?.value === "popup_contact_extended") {
      trackEvent({
        "dl.eAct": "ab.participated",
        "dl.eCat": page,
        "dl.eLab": "popup_contact_extended",
      });
    } else {
      trackEvent({
        "dl.eAct": "ab.participated",
        "dl.eCat": page,
        "dl.eLab": "original_popu_contact",
      });
    }
  }, [trackEvent, flag?.value, page]);

  useEffect(() => {
    if (isLocked) {
      let eCat = "search";
      if (isWatchdog) {
        eCat = "profile";
      }
      if (isRelated) {
        eCat = "offer_detail";
      }
      trackEvent({
        "dl.eCat": eCat,
        "dl.eAct": "dialog.shown",
        "dl.eLab": "Kontaktovat mohou pouze premium",
      });
    }
  }, []);

  const {
    registerWithError,
    getValues,
    handleSubmit,
    errors,
    setValue,
    watch,
    clearErrors,
  } = useForm<IFormValues>(
    isFlagAlternative ? FormSchemaBVariant : FormSchema,
    {
      onLoginChange: (props) => {
        if (props.user) {
          props.form.setValue("email", props.user.email);
          setValue(
            "phone",
            props.user?.renter?.phone ?? { prefix: "+420", number: "" },
          );
        }
      },
      formId: `contactModal_${offerId}`,
      defaultValues: initialValues,
    },
  );

  const phonePrexif = watch("phone.prefix");
  const phoneNumber = watch("phone.number");
  watch("availableFrom");

  const getInfo = useCallback((): void => {
    getContactInfoApi({ offerId }).then(({ data }) => {
      if (data) {
        setLoading(false);
        if ("externalUrl" in data) {
          setUrl(data as IExternalUrl);
        } else if (
          "premiumAccountCountdown" in data &&
          data.premiumAccountCountdown
        ) {
          setCountdown(data.premiumAccountCountdown);
        } else {
          setInfo(data as IContactInfo);
          if (data.noEmail) {
            trackEvent({
              "dl.eCat": "Kontaktovat",
              "dl.eAct": "load_popup_inzerat_bez_emailu",
            });
          }
          setIsEmail(!data.noEmail);
        }
      }
    });
  }, [offerId, getContactInfoApi, trackEvent]);

  useEffect(() => {
    getInfo();
  }, [getInfo]);

  const getFirstName = (firstName: string): string => {
    if (firstName) {
      return firstName;
    } else if (user) {
      return user.firstName;
    }

    return "";
  };
  const getLastName = (lastName: string): string => {
    if (lastName) {
      return lastName;
    } else if (user) {
      return user.lastName;
    }

    return "";
  };

  const onMessageSend = async (
    values: IFormValues,
    isregistered = true,
  ): Promise<void> => {
    const callEndpoint = async (): Promise<void> => {
      const { data } = await startConversationApi({
        message: values.message,
        email: values.email,
        firstName: getFirstName(values.firstName),
        lastName: getLastName(values.lastName),
        displayCard: values.includePersonalProfile,
        offerId,
        telephone: phonePrexif + phoneNumber || "",
      });

      if (values.isUpdateProfile && user?.renter) {
        const renterData = await changeRenterCardApi({
          ...user?.renter,
          lastName: values.lastName,
          firstName: values.firstName,
          birth: values.dateOfBirth,
          rentStartAt: values.availableFrom,
          informations: values.message,
          phone: { prefix: phonePrexif, number: phoneNumber },
        });
        if (renterData.data === "ok") {
          await refreshUser();
        }
      }
      if (data) {
        if (!isregistered) {
          trackEvent({
            "dl.eAct": "registration",
            "dl.eLab": `Odeslání zprávy – ${
              isRelated ? "detail inzerátu podobné" : "vyhledávání"
            }`,
            "dl.eCat": "",
          });
        }
        sendMessageConversion(getPageType(asPath));
        closeAllModals();
        setOffersContacted && setOffersContacted(offerId);
        openMessageSentModal();
        trackEvent({
          "dl.eCat": isWatchdog ? "profile" : "offer_detail",
          "dl.eAct": "dialog.shown",
          "dl.eLab": "Zpráva odeslána",
        });

        const isSaleOffer = asPath.includes("/prodej");

        trackEvent({
          "dl.eCat": "form.sent",
          "dl.eAct": isSaleOffer ? "inquiry_sale" : "inquiry_rent",
          "dl.eLab": "Odeslat",
          anonymousId: user?.id?.toString() || getUniqVisitorId(),
          category: "All",
        });
      }
    };

    await callEndpoint();
  };

  const extractDomain = (): string => {
    const a = document.createElement("a");
    a.href = url?.externalUrl as string;

    return a.hostname;
  };

  const handleData: OnFormSubmitType<IFormValues> = async (values) => {
    if (!user) {
      sessionStorage.setItem(
        ESessionItem.IS_REGISTER_FROM_CONTACT.toString(),
        Boolean(true).toString(),
      );
      if (values.email) {
        try {
          await isUserExistsApi({
            body: { email: values.email },
          }).unwrap();
        } catch {
          return onMessageSend(values, false);
        }
      }

      return openLoginModal({
        initialEmail: values.email,
        onSuccessfulLogin: () => onMessageSend(values),
        loginEvent,
      });
    }

    onMessageSend(values);
  };

  useEffect(() => {
    if (countdown) {
      countdown > 0 && setTimeout(() => setCountdown(countdown - 1), 1000);
      if (countdown === 1) {
        getContactInfoApi({ offerId }).then(({ data }) => {
          if (data) {
            setLoading(false);
            if ("externalUrl" in data) {
              setUrl(data as IExternalUrl);
            } else if (
              "premiumAccountCountdown" in data &&
              data.premiumAccountCountdown
            ) {
              setCountdown(data.premiumAccountCountdown);
            } else {
              setInfo(data as IContactInfo);
              if (data.noEmail) {
                trackEvent({
                  "dl.eCat": "Kontaktovat",
                  "dl.eAct": "load_popup_inzerat_bez_emailu",
                });
              }
              setIsEmail(!data.noEmail);
              setCountdown(undefined);
            }
          }
        });
      }
    }
  }, [countdown, offerId, getContactInfoApi, trackEvent]);

  const triggerLoginAndPayment = (): void =>
    openLoginModal({
      onSuccessfulLogin: () => {
        closeAllModals();
        navigateTo("/platba/premiovy-ucet");
      },
    });

  const handlePhoneClick = () => {
    trackEvent({
      "dl.eCat": "form.click",
      "dl.eAct": "phone_click",
      "dl.eLab": info?.phone || "",
      anonymousId: user?.id?.toString() || getUniqVisitorId(),
      category: "All",
    });
  };

  if (isLocked) {
    return (
      <Box>
        <Stack px="30px" pb="30px" align="center">
          <Heading textAlign="center" as="h2" variant="h3_1">
            Kontaktovat mohou pouze prémioví uživatelé
          </Heading>

          <Text py="10px" textAlign="center">
            přeskočit ostatní s{" "}
            <CustomLink
              to="/premium-vyhody"
              onClick={() => {
                let eCat = "search";
                if (isWatchdog) {
                  eCat = "profile";
                }
                if (isRelated) {
                  eCat = "offer_detail";
                }
                trackEvent({
                  "dl.eCat": eCat,
                  "dl.eAct": "link.clicked",
                  "dl.eLab": "Kontaktovat mohou pouze premium - Více o premium",
                });
              }}
            >
              Premium účtem
            </CustomLink>{" "}
            a kontaktovat ihned
          </Text>

          <CustomLink
            variant="button"
            to="/premium-vyhody"
            onClick={() => {
              let eCat = "search";
              if (isWatchdog) {
                eCat = "profile";
              }
              if (isRelated) {
                eCat = "offer_detail";
              }
              trackEvent({
                "dl.eCat": eCat,
                "dl.eAct": "premium.clicked",
                "dl.eLab":
                  "Kontaktovat mohou pouze premium - Aktivovat premium",
              });
            }}
          >
            Získat Premium účet
          </CustomLink>

          {!user && (
            <Text size="medium">
              Máte účet?{" "}
              <Button
                onClick={() => {
                  let eCat = "search";
                  if (isWatchdog) {
                    eCat = "profile";
                  }
                  if (isRelated) {
                    eCat = "offer_detail";
                  }
                  trackEvent({
                    "dl.eCat": eCat,
                    "dl.eAct": "link.clicked",
                    "dl.eLab": "Kontaktovat mohou pouze premium - Přihlášení",
                  });
                  const initialEmail = getValues().email;
                  openLoginModal({ initialEmail });
                }}
                variant="link"
                size="sm"
              >
                Přihlaste se
              </Button>
              .
            </Text>
          )}
        </Stack>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Stack h="200px">
        <CustomSpinner w="100%">
          <Text mt="80px" textAlign="center">
            Získáváme informace o majiteli...
          </Text>
        </CustomSpinner>
      </Stack>
    );
  }

  return (
    <Box>
      {countdown && (
        <Container minWidth="250px">
          <Text size="large" fontWeight="bold" textAlign="center">
            Kontaktovat mohou pouze <br /> prémioví uživatelé
          </Text>
          <Text textAlign="center" mt="10px" mb="12px">
            Počkat
          </Text>
          <Text size="large" fontWeight="bold" textAlign="center">
            {createTimeString(countdown)}
          </Text>
          <Stack my="12px" direction="row" align="center" spacing="16px">
            <Box bg="ud-grey-300" w="100%" h="1px" />
            <Text>nebo</Text>
            <Box bg="ud-grey-300" w="100%" h="1px" />
          </Stack>
          <Text textAlign="center" mb="30px">
            přeskočit ostatní s{" "}
            <CustomLink
              to={user ? "/platba/premiovy-ucet" : undefined}
              onClick={user ? closeAllModals : triggerLoginAndPayment}
            >
              Premium účtem
            </CustomLink>{" "}
            a kontaktovat ihned
          </Text>
          <Stack justify="center" direction="row" spacing={0}>
            <CustomLink
              trackEventPayload={{
                "dl.eLab": "kontaktovani_soukrome_inzerce",
                "dl.eAct": "premium_click",
                "dl.eCat": "premium ucet",
              }}
              to={user ? "/platba/premiovy-ucet" : undefined}
              onClick={user ? closeAllModals : triggerLoginAndPayment}
              variant="button"
            >
              <Stack direction="row" align="center">
                <IconCrown fillWith="ud-white" />
                <Text color="ud-white">Aktivovat Premium účet</Text>
              </Stack>
            </CustomLink>
          </Stack>
          {!user && (
            <Text textAlign="center" pt="16px">
              <i>
                Mám Premium účet.{" "}
                <Button
                  variant="link"
                  onClick={() =>
                    openLoginModal({
                      onSuccessfulLogin: () => {
                        getInfo();
                      },
                    })
                  }
                >
                  Přihlásit se
                </Button>
              </i>
            </Text>
          )}
        </Container>
      )}

      {info && (
        <>
          {info.isIn ? (
            <Stack align="center">
              <ContactAvatar
                name={
                  info.firstName || info.lastName
                    ? `${info.firstName || ""} ${info.lastName || ""}`
                    : "Jméno neuvedeno"
                }
                textTypeUser={info.companyName || "soukromý majitel"}
                photoUrl={info.photoUrl}
                phone={info.phone}
                isInOffer
                offerId={offerId}
                hasPhone={info.hasPhone}
                onPhoneClick={handlePhoneClick}
              />
              <FormIN offerId={offerId} />
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(handleData)} data-test="contactModal">
              <>
                <Stack mb="12px">
                  <ContactAvatar
                    name={
                      info.firstName || info.lastName
                        ? `${info.firstName || ""} ${info.lastName || ""}`
                        : "Jméno neuvedeno"
                    }
                    textTypeUser={info.companyName || "soukromý majitel"}
                    photoUrl={info.photoUrl}
                    phone={info.phone}
                    offerId={offerId}
                    hasPhone={info.hasPhone}
                    onPhoneClick={handlePhoneClick}
                  />
                </Stack>
                {isSuspicious && <SuspiciousOffer mb="10px" />}
                {!user && isEmail && (
                  <>
                    <Stack direction="row" justify="flex-end">
                      <Text variant="small_2">
                        Máte účet?{" "}
                        <Button
                          onClick={() => {
                            const initialEmail = getValues().email;
                            openLoginModal({
                              initialEmail,
                            });
                          }}
                          variant="link"
                          size="sm"
                        >
                          Přihlaste se
                        </Button>
                        .
                      </Text>
                    </Stack>
                    <AnimatedInput
                      {...registerWithError("email")}
                      type="email"
                      required
                      autoComplete="email"
                      label="Váš email "
                      dataTest="offerContact.email"
                    />
                  </>
                )}
                <Stack spacing={0}>
                  <PhoneInput
                    dataTest="offerContact.phone"
                    required
                    error={errors.phone}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    selectBoxValue={phonePrexif}
                    phoneNumberValue={phoneNumber}
                  />
                  {isFlagAlternative && (
                    <>
                      <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                        <AnimatedInput
                          {...registerWithError("firstName")}
                          type="text"
                          label="Jméno"
                          required
                          data-test="offerDetail.contactIN.name"
                        />
                        <AnimatedInput
                          {...registerWithError("lastName")}
                          label="Příjmení "
                          type="text"
                          required
                          data-test="offerDetail.contactIN.surname"
                        />
                      </Grid>
                      <Stack direction="row" className={styles.loop}>
                        <Stack flex={1}>
                          <AnimatedInput
                            label="rok narození"
                            {...registerWithError("dateOfBirth")}
                            type="number"
                            required
                            data-test="offerDetail.contactIN.date"
                          />
                        </Stack>
                        <Stack flex={1}>
                          <NewDatePicker
                            name="availableFrom"
                            isFutureDate
                            required
                            label="Od kdy chcete bydlet *"
                            dataTest="offerContact.availableFrom"
                            dateValue={
                              getValues("availableFrom")
                                ? new Date(getValues("availableFrom"))
                                : null
                            }
                            onDateChange={(date) => {
                              clearErrors("availableFrom");
                              setValue(
                                "availableFrom",
                                date?.toISOString() ?? "",
                              );
                            }}
                            error={errors.availableFrom}
                          />
                        </Stack>
                      </Stack>
                    </>
                  )}
                  {isEmail ? (
                    <>
                      <Textarea
                        {...registerWithError("message")}
                        label="Zpráva"
                        required
                        placeholder="Dobrý den, mám zájem o váš inzerát …"
                        dataTest="offerContact.message"
                        mb="25px"
                      />
                    </>
                  ) : (
                    <Text mb="20px" mt="30px">
                      <i>
                        Pronajímatel na sebe nezanechal emailový kontakt, lze ho
                        tedy kontaktovat pouze prostřednictvým telefonu.
                      </i>
                    </Text>
                  )}
                </Stack>

                <Checkbox
                  {...registerWithError("isUpdateProfile")}
                  textVariant="small_2"
                  label="Aktualizovat data v profilu."
                />

                <Text mb="20px" mt="10px" variant="small_2">
                  Kliknutím souhlasíte s našimi{" "}
                  <CustomLink showUnderline to="/zpracovani-osobnich-udaju">
                    zásadami ochrany osobních údajů.
                  </CustomLink>
                </Text>

                <Stack justify="center" alignItems="center">
                  {isEmail ? (
                    <Button
                      type="submit"
                      onClick={() => {
                        trackEvent({
                          "dl.eCat": isWatchdog ? "profile" : "offer_detail",
                          "dl.eAct": "message.clicked",
                          "dl.eLab": "Odeslat",
                        });
                      }}
                    >
                      Odeslat
                    </Button>
                  ) : (
                    <Button onClick={closeAllModals}>Zavřít</Button>
                  )}
                </Stack>
              </>
            </form>
          )}
        </>
      )}
      {url && (
        <Container minWidth="250px" maxWidth="400px">
          <Text mb="30px" textAlign="left">
            Tento inzerát jsme pro vás ulovili ve vodách{" "}
            <b>{extractDomain()}</b>. Pro jeho kontaktování vás přesměrujeme na
            jejich stránky.
          </Text>
          <Text mb="30px" textAlign="left">
            Chceme pro vás největší nabídku pronájmů na jednom místě. Pokud
            inzerát není aktuální, buďte prosím shovívaví, již pracujeme na jeho
            stažení.
          </Text>
          <Stack justify="center" direction="row">
            <CustomLink
              href={url?.externalUrl}
              isExternal
              trackEventPayload={{
                "dl.eCat": "Kontaktovat",
                "dl.eAct": "click_kontaktovat_sprateleny",
                "dl.eLab": extractDomain(),
              }}
              variant="button"
            >
              <Stack direction="row">
                <IconLink color="white" />
                <Text textColor="white" fontWeight="bold">
                  Kontaktovat na <i>{extractDomain()}</i>
                </Text>
              </Stack>
            </CustomLink>
          </Stack>
        </Container>
      )}
    </Box>
  );
};

export const useContactModal: InitialUseCreateModalType<
  ContactModalProps
> = () =>
  useCreateModal("contactModal", ContactModal as React.FC, null, null, {
    size: "lg",
    variant: "primary",
  });
